import React, { useCallback, useState, useEffect } from "react";
import { FilterFilled } from "@ant-design/icons";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { Map } from 'immutable';

import { clearUserValues, fetchUsers, fetchTotalUsersRequest } from "../../../redux/user/action";
import { data } from "../../../constants";
import { Users } from "../../templates/Users";
import { ButtonStyled, RenderText } from "./elements";
import { useHistory } from "react-router";
import { ADD_USER_REQUEST, UPDATE_USER_REQUEST, FETCH_USERS_REQUEST } from "../../../redux/user/actionType";
import { SEARCH_APPLICANT_REQUEST } from "../../../redux/applicant/actionTypes";
import { Alert } from "../../atoms/Alert";
import { clearSpecificRequest } from "../../../redux/request/action";
import { arrayToObject } from "../../../utils/helpers/transform";

const UsersPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [filterDropdown, setFilterDropdownVisible] = useState<any>({
    isVisible: false,
    dropdownKey: "",
  });

  const [usersData, setUsersData] = useState<any>({});
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastElement, setLastElement] = useState(undefined);
  const [filteredValue, setFilter] = useState<any[]>([]);

  const totalUsers = useSelector(
    (state: RootStateOrAny) => state.user.totalUsers,
  );

  const totalUsersLoading = useSelector(
    (state: RootStateOrAny) => state.user.totalUsersLoading,
  );
  
  const request = useSelector(
    (state: RootStateOrAny) => state.requestStore?.[ADD_USER_REQUEST]
  );

  const usersList = useSelector(
    (state: RootStateOrAny) => state.requestStore?.[FETCH_USERS_REQUEST]
  );

  const user = useSelector((state: RootStateOrAny) => state.auth.user);

  const defaultFilter = user.isSuperAdmin ? ['Admin', ...data.userAccessType] : data.userAccessType;

  const dispatchFetchUsers = useCallback(
    (
      lastElementDocs?: unknown,
      limit = 10,
      accessType = user.isSuperAdmin 
        ? ['recruiter', 'interviewer', 'admin'] 
        : ['recruiter', 'interviewer']
    ) =>
      dispatch(
        dispatch(fetchUsers({
          where: [
            ['accessType', 'in', accessType],
          ],
          orderBy: [['createdAt', 'desc']],
          limit,
          startAfter: lastElementDocs,
        })),
      ),
    [dispatch]
  );

  useEffect(() => {
    setFilter(defaultFilter);
  }, [user]);

  useEffect(() => {
    if (usersList && usersList?.response) {
      const lists = usersList.response.data;
      const arrToObj = Map(arrayToObject(lists));
      const mapUsersData = Map(usersData);
      const newUsersList = mapUsersData.mergeDeep(arrToObj);
      setUsersData(newUsersList.toJS());
      setLastElement(usersList.response.lastElement);
    }
  }, [usersList]);

  useEffect(() => {
    dispatchFetchUsers();
    dispatch(fetchTotalUsersRequest());
    
    dispatch(clearSpecificRequest(SEARCH_APPLICANT_REQUEST));
    dispatch(clearSpecificRequest(UPDATE_USER_REQUEST));
    return () => {
      dispatch(clearUserValues());
      dispatch(clearSpecificRequest(ADD_USER_REQUEST));
      dispatch(clearSpecificRequest(UPDATE_USER_REQUEST));
    }
  }, [dispatch]);

  const onClickCreateUser = () => {
    return history.push("/admin/users/create");
  };

  const onClickUpdate = (key: string) => {
    history.push(`/admin/users/${key}`);
  };

  const columns = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      width: "280px",
      render: (text: string, record: any) => (
        <RenderText isSuspended={record.status === "Suspended" && true}>
          {text}
        </RenderText>
      ),
    },
    {
      title: "Access Type",
      dataIndex: "accessType",
      key: "accessType",
      width: "180px",
      filterIcon: (
        <FilterFilled
          className={
            filterDropdown.dropdownKey === "accessType" &&
            filterDropdown.isVisible
              ? "tableFilterDropdownPositionActive"
              : ""
          }
        />
      ),
      filterDropdownVisible:
        filterDropdown.isVisible && filterDropdown.dropdownKey === "accessType",
      onFilterDropdownVisibleChange: (visible: any) => {
        setFilterDropdownVisible({
          isVisible: visible,
          dropdownKey: "accessType",
        });
      },
      filters: (defaultFilter).map((accessType: string) => {
        return { text: accessType, value: accessType };
      }),
      onFilter: (value: any, record: any) =>
        record.accessType.indexOf(value) === 0,
      render: (text: string, record: any) => (
        <RenderText isSuspended={record.status === "Suspended" && true}>
          {text}
        </RenderText>
      ),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      width: "230px",
      render: (text: string, record: any) => (
        <RenderText isSuspended={record.status === "Suspended" && true}>
          {text}
        </RenderText>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "180px",
      render: (text: string, record: any) => (
        <RenderText isSuspended={record.status === "Suspended" && true}>
          {text}
        </RenderText>
      ),
    },
    {
      title: "Actions",
      width: "200px",
      render: (text: string, record: any) => {
        return (
          <ButtonStyled onClick={() => onClickUpdate(record.key) as any}>
            Update
          </ButtonStyled>
        );
      },
    },
  ];

  const sortedUsers = Object.values(usersData).sort(
    (a: any, b: any) => {
      return (b.createdAt.toDate() - a.createdAt.toDate());
    },
  );

  const handleOnChange = (pagination: any, filters: any) => {
    const { current: page, pageSize: currPageSize } = pagination;
    const filteredAccessType = filters.accessType || undefined;
    if (filters.accessType) {
      setCurrentPage(1);
      setFilter(filteredAccessType);
    } else {
      setFilter(defaultFilter);
    }
    setPageSize(currPageSize);
    if (pageSize === currPageSize) {
      if (page > 1) {
        if (!(currentPage + 1 >= page) || !(currentPage - 1 <= page)) {
          dispatchFetchUsers(undefined, page * pageSize, filteredAccessType)
        } else {
          if (currentPage < page) {
            dispatchFetchUsers(lastElement, currPageSize, filteredAccessType);
          }
        }
      } else {
        dispatchFetchUsers(undefined, currPageSize, filteredAccessType);
      }
      setCurrentPage(page);
    } else {
      setCurrentPage(1);
      dispatchFetchUsers(undefined, page * currPageSize, filteredAccessType);
    }
  }

  const totalUsersSum = filteredValue.length ? filteredValue.map((val: string) => {
    return totalUsers[val.toLowerCase()]?.current; 
  }).reduce((a,b) => a + b) : 0;

  return (
    <>
      <Users
        onClickCreateUser={onClickCreateUser}
        isLoading={usersList?.isLoading || totalUsersLoading}
        tableData={sortedUsers}
        columns={columns}
        onChange={handleOnChange}
        total={totalUsersSum}
        currentPage={currentPage}
      />
      {request?.didSucceed &&
          request?.response && (
            <Alert 
              type="success"
              message="User successfully added"
              showIcon
            />
          )}
    </>
  );
};

export default UsersPage;
